import React, { Suspense, useMemo } from 'react';

import { Slider } from '@atlas/components';
import {
  constants,
  isEmptyCollection,
  isHidden,
  isSuspended,
  SiteConfigManager,
  useApplicationState,
  useBannerList,
  useEventsContext,
} from '@atlas/core';

import { filterBannersList } from 'common/utils/filterBannersList';
import { useIsRtl } from 'hooks/useIsRtl';

import TopEventBanner from './TopEventBanner';
import TopEventItem from './TopEventItem';
import TopEventsListLoader from './TopEventsListLoader';

const { SPORT_SERVICE } = constants;

const DEFAULT_TOP_EVENTS_SPEED = 8;

const getTopEventSlideList = ({ bannerStep = 2, banners = [], events = [] }) => {
  const mergedSlides = [...events];

  for (let i = 0; i < banners.length; i++) {
    const eventIndex = (i + 1) * bannerStep - 1;

    if (mergedSlides[eventIndex]) {
      mergedSlides.splice(eventIndex, 0, banners[i]);
    } else {
      mergedSlides[eventIndex] = banners[i];
    }
  }

  return mergedSlides.filter(Boolean);
};

const TopEventsSlider = () => {
  const { authenticated } = useApplicationState();
  const { events, isLoaded, isLoading } = useEventsContext();
  const { bannerList, isLoading: isBannerListLoading } = useBannerList(
    SiteConfigManager.getConfig('config.config.sportsbook.mobile_top_event_banner_group'),
    'MOBILE',
  );

  const isRtl = useIsRtl();

  const topEventsSpeed =
    SiteConfigManager.getConfig(
      'config.config.sportsbook.mobileTopEventsSpeed',
      DEFAULT_TOP_EVENTS_SPEED,
    ) * 1000;

  const isComponentLoading = (!isLoaded && isLoading) || isBannerListLoading;

  const filteredBanners = useMemo(
    () => filterBannersList(bannerList, authenticated).filter(({ image_url }) => !!image_url),
    [authenticated, bannerList],
  );

  const slides = React.useMemo(() => {
    if (isComponentLoading) {
      return [];
    }

    return getTopEventSlideList({
      bannerStep:
        SiteConfigManager.getConfig(
          'config.config.sportsbook.mobile_put_banner_after_each_nth_top_event',
          2,
        ) + 1,
      banners: filteredBanners,
      events: Object.values(events).filter(event => {
        const { isTop, sportService } = event;
        const isEventHidden =
          isHidden(event) || (isSuspended(event) && sportService === SPORT_SERVICE.ARCHIVED);

        return (isTop || isTop === undefined) && !isEventHidden;
      }),
    });
  }, [filteredBanners, events, isComponentLoading]);

  const sliderConfig = React.useMemo(
    () => ({
      autoplay: {
        delay: topEventsSpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      centeredSlides: true,
      dir: isRtl ? 'rtl' : 'ltr',
      loop: true,
    }),
    [isRtl, topEventsSpeed],
  );

  if (isComponentLoading) {
    return <TopEventsListLoader />;
  }

  if (isEmptyCollection(slides)) {
    return null;
  }

  return (
    <div className="sports-slider-container top-events">
      <Suspense fallback={<div />}>
        <Slider {...sliderConfig}>
          {slides.map(slide => {
            const { eventId } = slide;

            if (!eventId) {
              return (
                <Slider.Slide key={slide.title}>
                  <TopEventBanner banner={slide} />
                </Slider.Slide>
              );
            }

            return (
              <Slider.Slide key={eventId}>
                <TopEventItem event={slide} />
              </Slider.Slide>
            );
          })}
        </Slider>
      </Suspense>
    </div>
  );
};

export default TopEventsSlider;
